import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { ZoomTable, ZoomBtn } from './zoom/ZoomTable';

// Operations team (2026-10-23): members of the "Operations" group (day-to-day ops) and
// what they did — users added / deleted, customers onboarded, numbers changed, any
// other change, and calls made. Administrators only.
const err = (e: any) => e?.response?.data?.error || e?.message || 'Something went wrong';
const dubaiToday = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Dubai' }).format(new Date());
const hm = (s: number) => { s = Math.round(s || 0); const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60); return h ? `${h}h ${m}m` : `${m}m`; };
const box: React.CSSProperties = { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 16, marginBottom: 16 };

function useActivity(from: string, to: string, uid: string) {
  const [data, setData] = useState<any>(null);
  const [msg, setMsg] = useState('');
  const load = () => axios.get('/api/ops-activity', { params: { from, to, user_id: uid || undefined } }).then((r) => { setData(r.data); setMsg(''); }).catch((e) => { setData(null); setMsg(err(e)); });
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); }, [from, to, uid]); // eslint-disable-line react-hooks/exhaustive-deps
  return { data, msg, load };
}

function SummaryTable({ members }: { members: any[] }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
      <thead><tr>{['Team member', 'Calls made', 'Users added', 'Users deleted', 'Customers added', 'Number changes', 'All changes'].map((h) => (
        <th key={h} style={{ textAlign: h === 'Team member' ? 'left' : 'center', padding: '6px 8px', borderBottom: '1px solid #e5e7eb', color: '#6b7280', fontWeight: 600 }}>{h}</th>))}</tr></thead>
      <tbody>{members.map((m) => (
        <tr key={m.id}>
          <td style={{ padding: '6px 8px' }}><b>{m.name}</b>{m.ext ? <span style={{ color: '#6b7280' }}> · {m.ext}</span> : null}</td>
          <td style={{ textAlign: 'center' }}>{m.summary.calls}{m.summary.calls ? <span style={{ color: '#6b7280' }}> ({hm(m.summary.call_secs)})</span> : null}</td>
          <td style={{ textAlign: 'center' }}>{m.summary.users_added}</td>
          <td style={{ textAlign: 'center' }}>{m.summary.users_deleted}</td>
          <td style={{ textAlign: 'center' }}>{m.summary.customers_added}</td>
          <td style={{ textAlign: 'center' }}>{m.summary.numbers}</td>
          <td style={{ textAlign: 'center' }}><b>{m.summary.changes}</b></td>
        </tr>))}</tbody>
    </table>
  );
}
function EventList({ events, limit }: { events: any[]; limit?: number }) {
  const list = limit ? events.slice(0, limit) : events;
  if (!list.length) return <div style={{ color: '#6b7280', fontSize: 13 }}>No changes in this period.</div>;
  return (
    <div>{list.map((e) => (
      <div key={e.id} style={{ display: 'flex', gap: 10, padding: '6px 0', borderBottom: '1px solid #f1f5f9', fontSize: 13, alignItems: 'baseline' }}>
        <span style={{ color: '#6b7280', minWidth: 120 }}>{new Date(e.at).toLocaleString('en-GB', { timeZone: 'Asia/Dubai', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
        <b style={{ minWidth: 140 }}>{e.who}</b>
        <span style={{ flex: 1 }}>{e.action}{e.target ? <> — <b>{e.target}</b></> : null}{e.status >= 400 ? <span style={{ color: '#b91c1c' }}> (not completed)</span> : null}</span>
        <span style={{ color: '#9ca3af', fontSize: 12 }}>{e.area}</span>
      </div>))}</div>
  );
}

// Small card for the Dashboard / Wallboard (hidden when there is no Operations team)
export function OpsActivityCard() {
  const today = dubaiToday();
  const { data } = useActivity(today, today, '');
  if (!data || !data.members || !data.members.length) return null;
  return (
    <div style={{ ...box, marginTop: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <b style={{ fontSize: 15 }}>Operations team today</b>
        <span style={{ fontSize: 12, color: '#6b7280' }}>Full history: User Management › Access groups</span>
      </div>
      <SummaryTable members={data.members} />
      <div style={{ marginTop: 12, fontWeight: 600, fontSize: 13 }}>Latest changes</div>
      <EventList events={data.events || []} limit={8} />
    </div>
  );
}

export default function OperationsTeamPage() {
  const [tab, setTab] = useState<'activity' | 'members'>('activity');
  const [from, setFrom] = useState(dubaiToday());
  const [to, setTo] = useState(dubaiToday());
  const [uid, setUid] = useState('');
  const { data, msg } = useActivity(from, to, uid);
  const [prof, setProf] = useState<any>(null);
  const [pick, setPick] = useState('');
  const [pmsg, setPmsg] = useState('');
  const [gid, setGid] = useState('');
  const loadProf = () => axios.get('/api/access-profiles').then((r) => setProf(r.data)).catch((e) => setPmsg(err(e)));
  useEffect(() => { loadProf(); }, []);
  const ops = prof?.profiles?.find((p: any) => String(p.id) === gid) || prof?.profiles?.find((p: any) => p.name === 'Operations') || prof?.profiles?.[0];
  const add = async () => {
    if (!pick || !ops) return;
    try { const r = await axios.post(`/api/access-profiles/${ops.id}/members`, { user_id: Number(pick) }); setPick(''); setPmsg(r.data.note || 'Added'); loadProf(); }
    catch (e) { setPmsg(err(e)); }
  };
  const remove = async (m: any) => {
    if (!window.confirm(`Remove ${m.name} from the ${ops.name} team? Their admin access ends straight away.`)) return;
    try { await axios.delete(`/api/access-profiles/${ops.id}/members/${m.id}`); loadProf(); } catch (e) { setPmsg(err(e)); }
  };
  const tabBtn = (k: any, l: string) => <button onClick={() => setTab(k)} style={{ border: 'none', background: tab === k ? '#003A7A' : '#eef2f7', color: tab === k ? '#fff' : '#1e293b', borderRadius: 999, padding: '7px 16px', fontWeight: 600, cursor: 'pointer', marginRight: 6 }}>{l}</button>;

  return (
    <div>
      <div style={{ marginBottom: 14 }}>
        <h2 style={{ margin: '0 0 4px' }}>Access groups</h2>
        <div style={{ color: '#6b7280', fontSize: 13 }}>The day-to-day operations group: customers, PBX users, directory, extensions, voicemail, numbers, phones, queues (without changing members) and billing (accounts, invoices, reports). Everything the team changes is recorded here.</div>
      </div>
      <div style={{ marginBottom: 14 }}>{tabBtn('activity', 'Activity')}{tabBtn('members', `Members${ops ? ` (${ops.members.length})` : ''}`)}</div>

      {tab === 'activity' && <>
        <div style={{ ...box, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12 }}>From<br /><input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
          <label style={{ fontSize: 12 }}>To<br /><input type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
          <label style={{ fontSize: 12 }}>Team member<br /><select value={uid} onChange={(e) => setUid(e.target.value)}><option value="">Everyone</option>{(data?.members || []).map((m: any) => <option key={m.id} value={m.id}>{m.name}</option>)}</select></label>
          {msg && <span style={{ color: '#b91c1c', fontSize: 13 }}>{msg}</span>}
        </div>
        {data && <>
          <div style={box}>{data.members.length ? <SummaryTable members={uid ? data.members.filter((m: any) => String(m.id) === uid) : data.members} /> : <div style={{ color: '#6b7280' }}>Nobody is in the Operations team yet — add members on the Members tab.</div>}</div>
          <div style={box}><b style={{ display: 'block', marginBottom: 8 }}>Changes ({data.events.length})</b><EventList events={data.events} /></div>
        </>}
      </>}

      {tab === 'members' && ops && <>
        {(prof?.profiles || []).length > 1 && <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, color: '#475569' }}>Group:</span>
          {prof.profiles.map((g: any) => <button key={g.id} onClick={() => { setGid(String(g.id)); setPick(''); setPmsg(''); }} style={{ border: '1px solid ' + (g.id === ops.id ? '#003A7A' : '#cbd5e1'), background: g.id === ops.id ? '#003A7A' : '#fff', color: g.id === ops.id ? '#fff' : '#1e293b', borderRadius: 999, padding: '5px 14px', cursor: 'pointer', fontSize: 13 }}>{g.name} ({g.members.length})</button>)}
        </div>}
        {pmsg && <div style={{ ...box, padding: 10, color: '#1e293b' }}>{pmsg}</div>}
        <div style={{ ...box, display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <label style={{ fontSize: 12, flex: 1, minWidth: 240 }}>Add a team member<br />
            <select value={pick} onChange={(e) => setPick(e.target.value)} style={{ width: '100%' }}>
              <option value="">— choose a user —</option>{(prof?.candidates || []).filter((c: any) => Number(c.access_profile_id || 0) !== Number(ops.id)).map((c: any) => <option key={c.id} value={c.id}>{c.name} ({c.username}){c.access_group ? ` — move from ${c.access_group}` : ''}</option>)}
            </select></label>
          <ZoomBtn variant="primary" onClick={add}>Add to {ops.name}</ZoomBtn>
          <div style={{ fontSize: 12, color: '#6b7280', flexBasis: '100%' }}>Members get the {ops.name} group's menus and permissions from their next sign-in. Removing a member ends the access straight away and gives back their previous role.</div>
        </div>
        <ZoomTable title="" columns={[
          { key: 'n', label: 'Name', render: (m: any) => <div><div className="z-tbl-primary-cell">{m.name}</div><div className="z-tbl-sub">{m.username}{m.email ? ` · ${m.email}` : ''}</div></div> },
          { key: 'e', label: 'Extension', render: (m: any) => m.ext || '—' },
        ]} rows={ops.members} rowKey={(m: any) => m.id} rowActions={(m: any) => <ZoomBtn variant="danger" onClick={() => remove(m)}>Remove</ZoomBtn>} emptyText="No members yet" />
      </>}
      {tab === 'members' && !ops && <div style={box}>{pmsg || 'Loading…'}</div>}
    </div>
  );
}
